import { Tab } from '@design/components/tab-lines/TabLines';
import clsx from 'clsx';
import { useLayoutEffect, useRef } from 'react';

import styles from './EarningsTabPanel.module.scss';

const getTabId = (tabKey: string) => `revamp-earnings-tab-panel-tab-${tabKey}`;

export interface EarningsTabPanelProps<T> {
  tabs: Tab<T>[];
  activeTab: T;
  onChange: (index: number, tabKey: T) => void;
  className?: string;
  containedScroll?: boolean;
}

export const EarningsTabPanel = <T extends string>({
  tabs,
  activeTab,
  onChange,
  className,
  containedScroll = false,
}: EarningsTabPanelProps<T>) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const active = tabs.find(tab => tab.tabKey === activeTab);
  const currentPanel = active?.renderPanel;

  useLayoutEffect(() => {
    panelRef.current?.scrollTo(0, 0);
  }, [activeTab]);

  return (
    <div
      className={clsx(styles.root, className, {
        [styles.contained]: containedScroll,
      })}
      data-testid="revamp-earnings-tab-panel"
    >
      <div className={clsx({ [styles.tabFrame]: containedScroll })}>
        <div className={clsx({ [styles.tabCentered]: containedScroll })}>
          <div className={styles.tablist} role="tablist">
            {tabs.map(({ tabKey, tabLabel }, index) => {
              const tabId = getTabId(tabKey);

              return (
                <button
                  type="button"
                  key={tabKey}
                  className={clsx(styles.tab, {
                    [styles.tabActive]: activeTab === tabKey,
                  })}
                  data-testid={tabId}
                  role="tab"
                  aria-selected={activeTab === tabKey}
                  aria-controls={tabId}
                  onClick={() => onChange(index, tabKey)}
                >
                  {tabLabel}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div
        ref={panelRef}
        className={clsx(styles.tabPanel, {
          [styles.containedScroll]: containedScroll,
        })}
        data-testid="revamp-earnings-tab-panel-content"
        data-earnings-insights-scroll={containedScroll ? '' : undefined}
        role="tabpanel"
        id={getTabId(activeTab)}
        aria-label={activeTab}
      >
        <div className={clsx({ [styles.panelCentered]: containedScroll })}>
          {currentPanel}
        </div>
      </div>
    </div>
  );
};
